import { useEffect, useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { AuthContext } from '@/App';
import axios from 'axios';
import { Calendar as CalendarIcon, Plus, School } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '@/styles/calendar.css';
import AddLifeEventModal from '@/components/AddLifeEventModal';
import AddSchoolScheduleModal from '@/components/AddSchoolScheduleModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AllCoursesCalendar() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [courses, setCourses] = useState([]);
  const [syllabusData, setSyllabusData] = useState({});
  const [manualEvents, setManualEvents] = useState({});
  const [lifeEvents, setLifeEvents] = useState([]);
  const [classSchedule, setClassSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState('month');
  const [showAddLifeEventModal, setShowAddLifeEventModal] = useState(false);
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  
  const localizer = useMemo(() => momentLocalizer(moment), []);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      const [statusRes, coursesRes] = await Promise.all([
        axios.get(`${API}/study-buddy/subscription-status`, { withCredentials: true }),
        axios.get(`${API}/study-buddy/courses`, { withCredentials: true })
      ]);
      
      setSubscriptionStatus(statusRes.data);
      
      // Check if user has subscription
      if (!statusRes.data.has_subscription) {
        toast.error('Premium subscription required to access Calendar');
        navigate('/study-buddy/subscribe');
        return;
      }

      setCourses(coursesRes.data);

      // Load syllabus data for each course
      const syllabusPromises = coursesRes.data.map(course =>
        axios.get(`${API}/study-buddy/courses/${course.id}/syllabus`, { withCredentials: true })
          .then(res => ({ courseId: course.id, data: res.data }))
          .catch(() => ({ courseId: course.id, data: null }))
      );

      const syllabusResults = await Promise.all(syllabusPromises);
      
      const syllabusMap = {};
      syllabusResults.forEach(result => {
        if (result.data) {
          syllabusMap[result.courseId] = result.data;
        }
      });

      setSyllabusData(syllabusMap);

      // Load manual events for each course
      const manualEventsPromises = coursesRes.data.map(course =>
        axios.get(`${API}/study-buddy/courses/${course.id}/manual-events`, { withCredentials: true })
          .then(res => ({ courseId: course.id, data: res.data }))
          .catch(() => ({ courseId: course.id, data: [] }))
      );

      const manualEventsResults = await Promise.all(manualEventsPromises);
      
      const manualEventsMap = {};
      manualEventsResults.forEach(result => {
        manualEventsMap[result.courseId] = result.data;
      });

      setManualEvents(manualEventsMap);

      // Load life events (premium feature)
      try {
        const lifeEventsRes = await axios.get(`${API}/study-buddy/life-events`, { withCredentials: true });
        setLifeEvents(lifeEventsRes.data);
      } catch (error) {
        // User might not have premium or no life events yet
        setLifeEvents([]);
      }

      // Load class schedule (premium feature)
      try {
        const scheduleRes = await axios.get(`${API}/study-buddy/class-schedule`, { withCredentials: true });
        setClassSchedule(scheduleRes.data);
      } catch (error) {
        // User might not have premium or no schedule yet
        setClassSchedule([]);
      }

    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load courses data');
    } finally {
      setLoading(false);
    }
  };

  // Get all calendar events from all courses
  const getAllCalendarEvents = () => {
    const events = [];
    
    courses.forEach(course => {
      const syllabus = syllabusData[course.id];
      if (syllabus && syllabus.items) {
        syllabus.items.forEach(item => {
          if (item.due_date) {
            // Use moment to parse date consistently with individual calendar
            const dueDate = moment(item.due_date).toDate();
            events.push({
              id: item.item_id,
              title: `${course.course_name}: ${item.title}`,
              start: dueDate,
              end: dueDate,
              type: item.item_type || 'other',
              courseName: course.course_name,
              courseId: course.id,
              description: item.description,
              allDay: true
            });
          }
        });
      }

      // Add manual events for this course
      const courseManualEvents = manualEvents[course.id] || [];
      courseManualEvents.forEach(event => {
        // Use moment to parse date consistently
        const dueDate = moment(event.due_date).toDate();
        events.push({
          id: `manual-${event.id}`,
          title: `${course.course_name}: ✏️ ${event.title}`,
          start: dueDate,
          end: dueDate,
          type: event.event_type,
          courseName: course.course_name,
          courseId: course.id,
          description: event.description,
          allDay: true
        });
      });
    });

    // Add life events
    lifeEvents.forEach(event => {
      // Use moment to parse dates consistently
      const eventDate = moment(event.event_date).toDate();
      const endDate = event.end_date ? moment(event.end_date).toDate() : eventDate;
      
      events.push({
        id: `life-${event.id}`,
        title: `${event.title}`,
        start: eventDate,
        end: endDate,
        type: event.event_type,
        description: event.description,
        allDay: event.all_day
      });
    });

    // Add class schedule (recurring weekly events)
    classSchedule.forEach(scheduleItem => {
      // Get the current view date range - extend to show more weeks
      const viewStart = moment(currentDate).startOf('month').subtract(1, 'week').toDate();
      const viewEnd = moment(currentDate).endOf('month').add(2, 'weeks').toDate();
      
      // Generate events for each day in the view range
      let current = moment(viewStart);
      while (current.isBefore(viewEnd)) {
        const dayName = current.format('dddd').toLowerCase();
        
        if (scheduleItem.days_of_week && scheduleItem.days_of_week.includes(dayName)) {
          // Parse times - ensure they're valid
          const startTimeParts = scheduleItem.start_time.split(':');
          const endTimeParts = scheduleItem.end_time.split(':');
          
          if (startTimeParts.length >= 2 && endTimeParts.length >= 2) {
            const startHour = parseInt(startTimeParts[0]);
            const startMin = parseInt(startTimeParts[1]);
            const endHour = parseInt(endTimeParts[0]);
            const endMin = parseInt(endTimeParts[1]);
            
            // Create new moment instances to avoid mutation
            const startTime = moment(current).hour(startHour).minute(startMin).second(0).toDate();
            const endTime = moment(current).hour(endHour).minute(endMin).second(0).toDate();
            
            events.push({
              id: `schedule-${scheduleItem.id}-${current.format('YYYY-MM-DD')}`,
              title: `🏫 ${scheduleItem.class_name}${scheduleItem.location ? ` - ${scheduleItem.location}` : ''}`,
              start: startTime,
              end: endTime,
              type: 'class_schedule',
              description: scheduleItem.instructor || '',
              allDay: false
            });
          }
        }
        
        current = moment(current).add(1, 'day');
      }
    });

    return events;
  };

  // Style events based on type
  const eventStyleGetter = (event) => {
    const colors = {
      assignment: { backgroundColor: '#3B82F6', borderColor: '#2563EB' },
      exam: { backgroundColor: '#EF4444', borderColor: '#DC2626' },
      quiz: { backgroundColor: '#F59E0B', borderColor: '#D97706' },
      project: { backgroundColor: '#8B5CF6', borderColor: '#7C3AED' },
      reading: { backgroundColor: '#10B981', borderColor: '#059669' },
      study_session: { backgroundColor: '#06B6D4', borderColor: '#0891B2' },
      work: { backgroundColor: '#F97316', borderColor: '#EA580C' },
      social: { backgroundColor: '#EC4899', borderColor: '#DB2777' },
      personal: { backgroundColor: '#6366F1', borderColor: '#4F46E5' },
      class_schedule: { backgroundColor: '#14B8A6', borderColor: '#0D9488' },
      other: { backgroundColor: '#6B7280', borderColor: '#4B5563' }
    };

    const style = colors[event.type] || colors.other;
    
    return {
      style: {
        ...style,
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        padding: '2px 5px',
        fontSize: '0.875rem'
      }
    };
  };

  const handleSelectEvent = (event) => {
    // Only navigate to course if the event is associated with a course
    if (event.courseId) {
      navigate(`/study-buddy/course/${event.courseId}`);
    } else {
      // For life events and class schedule, show a toast with details
      const eventDetails = event.description 
        ? `${event.title}\n${event.description}`
        : event.title;
      toast.info(eventDetails, { duration: 3000 });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Layout>
      <section className="min-h-screen py-16 bg-gradient-to-b from-[#F4E4C1]/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/study-buddy/my-courses')}
              className="text-[#2C1810] hover:text-[#E85D4D] font-semibold mb-4 inline-flex items-center"
            >
              ← Back to My College Courses
            </button>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold retro-title text-[#2C1810] mb-4">
                  All Courses Calendar
                </h1>
                <p className="text-xl text-gray-700">
                  View all your course deadlines and events in one unified calendar
                </p>
              </div>
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setShowAddScheduleModal(true)}
                  className="px-6 py-3 bg-teal-600 text-white font-bold rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 shadow-lg"
                >
                  <School className="w-5 h-5" />
                  School Schedule
                </button>
                <button
                  onClick={() => setShowAddLifeEventModal(true)}
                  className="px-6 py-3 bg-pink-600 text-white font-bold rounded-lg hover:bg-pink-700 transition-colors flex items-center gap-2 shadow-lg"
                >
                  <Plus className="w-5 h-5" />
                  Add Life Event
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E8934D]"></div>
            </div>
          ) : (
            <>
              {/* Calendar Section */}
              {getAllCalendarEvents().length > 0 ? (
                <div className="cassette-card p-6">
                  <div className="bg-white rounded-lg p-4" style={{ height: '700px' }}>
                    <Calendar
                      localizer={localizer}
                      events={getAllCalendarEvents()}
                      startAccessor="start"
                      endAccessor="end"
                      style={{ height: '100%' }}
                      onSelectEvent={handleSelectEvent}
                      eventPropGetter={eventStyleGetter}
                      views={['month', 'week', 'agenda']}
                      date={currentDate}
                      onNavigate={(date) => setCurrentDate(date)}
                      view={currentView}
                      onView={(view) => setCurrentView(view)}
                    />
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-6 pt-6 border-t border-[#E8B67A]">
                    <h3 className="text-sm font-bold text-[#2C1810] mb-3">Event Types:</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-500 rounded"></div>
                        <span className="text-sm text-gray-700">Assignment</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-red-500 rounded"></div>
                        <span className="text-sm text-gray-700">Exam</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                        <span className="text-sm text-gray-700">Quiz</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-purple-500 rounded"></div>
                        <span className="text-sm text-gray-700">Project</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-green-500 rounded"></div>
                        <span className="text-sm text-gray-700">Reading</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-cyan-500 rounded"></div>
                        <span className="text-sm text-gray-700">Study Session</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-orange-500 rounded"></div>
                        <span className="text-sm text-gray-700">Work</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-pink-500 rounded"></div>
                        <span className="text-sm text-gray-700">Social</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-indigo-500 rounded"></div>
                        <span className="text-sm text-gray-700">Personal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-teal-500 rounded"></div>
                        <span className="text-sm text-gray-700">Class Schedule 🏫</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="cassette-card p-12 text-center">
                  <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-400 opacity-50" />
                  <h3 className="text-xl font-bold text-gray-600 mb-2">No Events Yet</h3>
                  <p className="text-gray-500 mb-6">Upload syllabi to your courses to see all deadlines in one unified calendar.</p>
                  <button
                    onClick={() => navigate('/study-buddy')}
                    className="px-6 py-3 bg-[#E8934D] text-white font-bold rounded-lg hover:bg-[#E85D4D] transition-colors"
                  >
                    Go to Study Buddy
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      
      {/* Add School Schedule Modal */}
      {showAddScheduleModal && (
        <AddSchoolScheduleModal
          onClose={() => setShowAddScheduleModal(false)}
          onSuccess={() => {
            setShowAddScheduleModal(false);
            loadData();
          }}
        />
      )}
      
      {/* Add Life Event Modal */}
      {showAddLifeEventModal && (
        <AddLifeEventModal
          onClose={() => setShowAddLifeEventModal(false)}
          onSuccess={() => {
            setShowAddLifeEventModal(false);
            loadData();
          }}
        />
      )}
    </Layout>
  );
}
